import {
  collection,
  count,
  doc,
  getAggregateFromServer,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  sum,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

/**
 * Leitura do Firestore num lugar só.
 *
 * O painel LÊ direto do Firestore e ESCREVE por Cloud Function (ver
 * `lib/callables.ts`). Nenhuma tela chama `getDocs` por conta própria: sem
 * isso, trocar a forma de um documento vira caçada por `collection(` em vinte
 * arquivos, que é exatamente o problema que os dois aplicativos evitaram com a
 * camada de serviços.
 */

export interface WithId {
  id: string;
}

export async function fetchCollection<T extends WithId>(
  path: string,
  ...constraints: QueryConstraint[]
): Promise<T[]> {
  const snap = await getDocs(query(collection(db, path), ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T);
}

export async function fetchDocument<T extends WithId>(
  path: string,
  id: string,
): Promise<T | null> {
  const snap = await getDoc(doc(db, path, id));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as T) : null;
}

export interface PageResult<T> {
  rows: T[];
  /** O último documento da página, que vira o cursor da próxima. */
  cursor: QueryDocumentSnapshot | null;
  hasMore: boolean;
}

/**
 * Uma página de uma consulta ordenada, por cursor.
 *
 * Só serve a consultas cujo campo de ordenação o backend ou o aplicativo grava
 * em TODO documento (`orders.createdAt`, `adminNotifications.sentAt`): o
 * Firestore omite o documento a que falta esse campo. As listas de cadastro
 * continuam inteiras e paginadas em memória (ver `hooks/use-pagination.ts`).
 *
 * Pede um item a mais para saber se existe próxima página sem uma segunda
 * consulta.
 */
export async function fetchPage<T extends WithId>(
  path: string,
  constraints: QueryConstraint[],
  pageSize: number,
  after: QueryDocumentSnapshot | null,
): Promise<PageResult<T>> {
  const parts = [...constraints];
  if (after) parts.push(startAfter(after));
  parts.push(fsLimit(pageSize + 1));

  const snap = await getDocs(query(collection(db, path), ...parts));
  const docs = snap.docs.slice(0, pageSize);
  return {
    rows: docs.map((d) => ({ id: d.id, ...d.data() }) as T),
    cursor: docs.length ? docs[docs.length - 1] : null,
    hasMore: snap.docs.length > pageSize,
  };
}

/**
 * Contagem e soma calculadas pelo servidor, sem trazer os documentos.
 *
 * É o que permite a tela mostrar "1.234 pedidos, R$ 56 mil" enquanto lista só
 * vinte e cinco: a agregação custa uma leitura a cada mil entradas de índice,
 * e não uma por documento.
 */
export async function fetchAggregate(
  path: string,
  constraints: QueryConstraint[],
  sumField?: string,
): Promise<{ count: number; sum: number }> {
  const q = query(collection(db, path), ...constraints);
  const snap = sumField
    ? await getAggregateFromServer(q, { count: count(), sum: sum(sumField) })
    : await getAggregateFromServer(q, { count: count() });
  const data = snap.data() as { count: number; sum?: number };
  return { count: data.count, sum: Number(data.sum ?? 0) };
}

export { orderBy, where, fsLimit as limit };
